import React, { useState } from 'react';
import {
  Box,
  Rating,
  Dialog,
  Typography,
  IconButton
} from '@mui/material';
import { toast } from 'react-toastify';
import { API, IReviewData } from '../../../../api';
import { events, attributeKeys } from '../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../utils/amplitudeProductTracking';
import VoteIcons from '../../components/VoteIcons';
import { getUserDetails } from '../../../../utils/orgName';
import useResponsiveScreenSwitch from '../../../../utils/useResponsiveScreenSwitch';
import { inputCharacterCount, showCharacterCountWarning } from '../../components/common';
import useProfileDetails from '../../../../hooks/useProfileDetails';
import ROLES from '../../../../utils/roles';
import { useMemberWord } from '../../../../utils/memberTypeLabels';
import CloseIcon from '@mui/icons-material/Close';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DosenInput from '../../components/DosenInput';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';

interface PostCallPopupProps {
  title: string;
  meetingId: string;
  reviewParticipant: any;
  openPostCallPopup: boolean;
  handleClosePostCallPopup: () => void;
  name: string;
}

const PostCallPopup: React.FC<PostCallPopupProps> = ({
  title,
  meetingId,
  reviewParticipant,
  openPostCallPopup,
  handleClosePostCallPopup,
  name
}) => {
  const openModal = openPostCallPopup;
  const { forMobile } = useResponsiveScreenSwitch();
  const { location, userId } = getUserDetails();
  const [isLoading, setLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [upDownVoteValue, setUpDownVoteValue] = useState(0);
  const [value, setValue] = React.useState<number>(0);
  const { mentee } = useProfileDetails();
  const role = localStorage.getItem('role') || '';
  const isMentee = (Array.isArray(mentee) && mentee.length > 0) || role === 'mentee';
  const w = useMemberWord();
  const mentorWord = w('mentor', { lower: true });
  const menteeWord = w('mentee', { lower: true });
  const isVisible = true;

  /** Review Count */
  const maxCharacterCount = 1000;
  const totalCharactersReview = inputCharacterCount(description, maxCharacterCount);
  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    showCharacterCountWarning(newValue, maxCharacterCount);
    setDescription(newValue);
  };

  const handleVote = (voteValue: number) => {
    setUpDownVoteValue(voteValue);
    // if (voteValue === 0) {
    //   // closes the modal
    //   handleSubmit(voteValue);
    // }
  };

  const handleSubmit = async () => {
    // The star rating is required (mentee view only — mentors have no stars).
    if (isMentee && value === 0) {
      toast.error('Please rate the call before saving.');
      return;
    }
    const receivedVoteIds: string[] = reviewParticipant.map((reviewParticipant: { mid: any }) => reviewParticipant.mid);
    let data: IReviewData = {
      vote: upDownVoteValue,
      score: value,
      isReviewed: true,
      participantUsers: receivedVoteIds
    };
    if (isVisible) {
      data = { ...data, review: description };
    }
    try {
      setLoading(true);
      const response = await API.addReview({ data, meetingId });
      if (response?.status === 200) {
        toast.success('Post call notes completed');
        const od = getUserDetails();
        // Full base context (org type, program, user role/email, last login) +
        // meeting specifics. NB: the star rating is a meeting rating — it was
        // previously mislabelled as MCQ Score.
        trackWithBaseProps(events.meetingCompleted, {
          [attributeKeys.organizationName]: od.orgName || '',
          meetingId,
          meetingRating: value || undefined,
          helpfulVote: upDownVoteValue
        });
      }
    } catch (e) {
      //toast.error(_.get(e, 'response.data.message', `Couldn't update review`));
      // Close the current popup on error
    } finally {
      setLoading(false);
      handleClosePostCallPopup();
    }
  };

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent the dialog from closing when clicking on the backdrop
    event.stopPropagation();
  };

  if (!openModal) return null;

  return (
    <Dialog
      open
      onClose={() => {}}
      disableEscapeKeyDown
      onClick={handleBackdropClick}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: forMobile ? 'calc(100% - 24px)' : 550,
          borderRadius: 2,
          background: 'var(--Fill-Modal-background, #0F0F0F)',
          outline: '1px solid var(--Stroke-Modal-border, #2A2A2A)',
          outlineOffset: '-1px',
          m: forMobile ? 1.5 : 3,
          overflow: 'hidden'
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        <Typography
          sx={{
            flex: 1,
            color: 'var(--Text-text-primary, #FDFDFD)',
            fontSize: 18,
            fontWeight: 700,
            fontFamily: 'Inter'
          }}
        >
          {title}
        </Typography>
        <IconButton
          // Dismissing must NOT count as a review — nothing is written, so the
          // popup comes back on the next visit until the user actually saves.
          onClick={handleClosePostCallPopup}
          sx={{
            width: 20,
            height: 20,
            p: 0.5,
            color: 'var(--Stroke-icon-highlight, #FDFDFD)'
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3
        }}
      >
        {isMentee && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: forMobile ? 'column' : 'row',
              gap: 3
            }}
          >
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ color: 'var(--Text-text-primary, #FDFDFD)', fontSize: 14, fontWeight: 500 }}>
                How helpful was the call?
              </Typography>
              <Rating
                name="post-call-rating"
                sx={{
                  // Don't let the parent column's `align-items: stretch` widen the
                  // Rating root: MUI maps hover position across the full root width,
                  // so a stretched root makes the highlighted star lag to the right
                  // of the cursor.
                  alignSelf: 'flex-start',
                  gap: '2px',
                  '& .MuiRating-icon': {
                    padding: 0,
                    margin: 0
                  },
                  '& .MuiRating-iconEmpty': {
                    color: '#565656'
                  }
                }}
                value={value}
                onChange={(event, newValue) => {
                  setValue(newValue ? newValue : 0);
                }}
                icon={<StarBorderIcon sx={{ color: '#FDFDFD', fontSize: 20, display: 'block' }} />}
                emptyIcon={<StarBorderIcon sx={{ color: '#565656', fontSize: 20, display: 'block' }} />}
              />
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography sx={{ color: 'var(--Text-text-primary, #FDFDFD)', fontSize: 14, fontWeight: 500 }}>
                Was your {mentorWord} helpful?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <IconButton
                  onClick={() => handleVote(1)}
                  sx={{
                    p: 0,
                    color: upDownVoteValue === 1 ? '#FDFDFD' : '#868686'
                  }}
                >
                  <VoteIcons voteValue={upDownVoteValue} />
                </IconButton>
                <IconButton
                  onClick={() => handleVote(2)}
                  sx={{
                    p: 0,
                    color: upDownVoteValue === 2 ? '#FDFDFD' : '#868686'
                  }}
                >
                  <VoteIcons voteValue={upDownVoteValue} icon="dislike" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {isVisible && (
          <DosenInput
            label="Add notes"
            value={description}
            onChange={handleDescriptionChange}
            multiline
            rows={4}
            maxChars={totalCharactersReview?.maxCharacterCount}
            placeholder={
              isMentee
                ? `These notes are shared with both you and your ${mentorWord}. Use them to capture the key discussion points, decisions, action items, and any important takeaways from your session.`
                : `These notes are shared with both you and your ${menteeWord}. Use them to capture any information you'd like you or your ${menteeWord} to refer back to after your session`
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.04) !important',
                borderRadius: '6px',
                '& fieldset': {
                  borderColor: 'var(--Stroke-Border-border-highlight, #2A2A2A) !important'
                },
                '&:hover fieldset': {
                  borderColor: 'var(--Stroke-Border-border-highlight, #2A2A2A) !important'
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--Stroke-Border-border-highlight, #2A2A2A) !important'
                }
              },
              '& .MuiInputBase-input': {
                minHeight: '56px'
              }
            }}
          />
        )}

        <DosenButton
          sizeVariant="sm"
          onClick={handleSubmit}
          loading={isLoading}
          loadingText="Saving..."
          disabled={isLoading}
          sx={{ width: 'fit-content', alignSelf: 'center' }}
        >
          Save Review
        </DosenButton>
      </Box>
    </Dialog>
  );
};

export default PostCallPopup;
